import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import scheduleApiService from '@/services/scheduleApi';
import {
  Schedule,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  ScheduleWithValvesResponse,
  ScheduleValidationResult,
  ValveInfo
} from '@/types/schedule';
import { DeviceGroup, SchedulePlan } from '@/types/schedule';

interface ScheduleState {
  // Simple schedules (new implementation)
  schedules: Schedule[];
  schedulesWithValves: ScheduleWithValvesResponse[];
  valves: ValveInfo[];

  // Legacy schedules (backward compatibility)
  list: SchedulePlan[];
  groups: DeviceGroup[];

  // UI state
  loading: boolean;
  error: string | null;
  selectedSchedule: Schedule | null;
  validationResults: Record<string, ScheduleValidationResult>;

  // Real-time updates
  lastUpdated: number | null;
  connectionStatus: 'connected' | 'disconnected' | 'error' | 'connecting';

  // Filters and search
  filters: {
    activeOnly: boolean;
    searchTerm: string;
    selectedDays: string[];
  };
}

const initialState: ScheduleState = {
  // Simple schedules
  schedules: [],
  schedulesWithValves: [],
  valves: [],

  // Legacy
  list: [],
  groups: [],

  // UI state
  loading: false,
  error: null,
  selectedSchedule: null,
  validationResults: {},

  // Real-time updates
  lastUpdated: null,
  connectionStatus: 'disconnected',

  // Filters
  filters: {
    activeOnly: false,
    searchTerm: '',
    selectedDays: [],
  },
};

// Simple Schedule Async Thunks
export const fetchSchedulesAsync = createAsyncThunk(
  'schedules/fetchSchedules',
  async (activeOnly: boolean = false, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.getSchedules(activeOnly);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch schedules');
    }
  }
);

export const fetchSchedulesWithValvesAsync = createAsyncThunk(
  'schedules/fetchSchedulesWithValves',
  async (activeOnly: boolean = false, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.getSchedulesWithValves(activeOnly);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch schedules with valves');
    }
  }
);

export const fetchScheduleAsync = createAsyncThunk(
  'schedules/fetchSchedule',
  async (scheduleId: string, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.getSchedule(scheduleId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch schedule');
    }
  }
);

export const createScheduleAsync = createAsyncThunk(
  'schedules/createSchedule',
  async (scheduleData: CreateScheduleRequest, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.createSchedule(scheduleData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create schedule');
    }
  }
);

export const updateScheduleAsync = createAsyncThunk(
  'schedules/updateSchedule',
  async ({ scheduleId, scheduleData }: { scheduleId: string; scheduleData: UpdateScheduleRequest }, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.updateSchedule(scheduleId, scheduleData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update schedule');
    }
  }
);

export const deleteScheduleAsync = createAsyncThunk(
  'schedules/deleteSchedule',
  async (scheduleId: string, { rejectWithValue }) => {
    try {
      await scheduleApiService.deleteSchedule(scheduleId);
      return scheduleId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete schedule');
    }
  }
);

export const toggleScheduleAsync = createAsyncThunk(
  'schedules/toggleSchedule',
  async ({ scheduleId, active }: { scheduleId: string; active: boolean }, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.toggleSchedule(scheduleId, active);
      return { scheduleId, active, response: response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to toggle schedule');
    }
  }
);

export const validateScheduleAsync = createAsyncThunk(
  'schedules/validateSchedule',
  async (scheduleData: CreateScheduleRequest, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.validateSchedule(scheduleData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to validate schedule');
    }
  }
);

export const validateExistingScheduleAsync = createAsyncThunk(
  'schedules/validateExistingSchedule',
  async (scheduleId: string, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.validateExistingSchedule(scheduleId);
      return { scheduleId, result: response };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to validate schedule');
    }
  }
);

export const fetchValvesAsync = createAsyncThunk(
  'schedules/fetchValves',
  async (_, { rejectWithValue }) => {
    try {
      const response = await scheduleApiService.getValves();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch valves');
    }
  }
);

// Legacy Async Thunks (backward compatibility)
export const fetchDeviceGroupsAsync = createAsyncThunk(
  'schedules/fetchDeviceGroups',
  async (_, { rejectWithValue }) => {
    try {
      // This would need to be implemented in the API service if needed
      // const response = await apiService.getDeviceGroups();
      // return response.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch device groups');
    }
  }
);

const scheduleSlice = createSlice({
  name: 'schedules',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedSchedule: (state, action: PayloadAction<Schedule | null>) => {
      state.selectedSchedule = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<ScheduleState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearValidationResults: (state) => {
      state.validationResults = {};
    },
    // Real-time update reducers
    setConnectionStatus: (state, action: PayloadAction<'connected' | 'disconnected' | 'error' | 'connecting'>) => {
      state.connectionStatus = action.payload;
    },
    updateScheduleFromWebSocket: (state, action: PayloadAction<Schedule>) => {
      const updatedSchedule = action.payload;
      const index = state.schedules.findIndex(s => s.id === updatedSchedule.id);

      if (index !== -1) {
        // Update existing schedule
        state.schedules[index] = updatedSchedule;
      } else {
        // Add new schedule
        state.schedules.push(updatedSchedule);
      }

      // Update schedules with valves if present
      const valvesIndex = state.schedulesWithValves.findIndex(s => s.id === updatedSchedule.id);
      if (valvesIndex !== -1) {
        state.schedulesWithValves[valvesIndex] = {
          ...state.schedulesWithValves[valvesIndex],
          ...updatedSchedule,
        };
      }

      // Update selected schedule if it's the one being updated
      if (state.selectedSchedule?.id === updatedSchedule.id) {
        state.selectedSchedule = updatedSchedule;
      }

      state.lastUpdated = Date.now();
    },
    removeScheduleFromWebSocket: (state, action: PayloadAction<string>) => {
      const scheduleId = action.payload;

      // Remove from schedules
      state.schedules = state.schedules.filter(s => s.id !== scheduleId);

      // Remove from schedules with valves
      state.schedulesWithValves = state.schedulesWithValves.filter(s => s.id !== scheduleId);

      // Clear selected schedule if it's the one being removed
      if (state.selectedSchedule?.id === scheduleId) {
        state.selectedSchedule = null;
      }

      state.lastUpdated = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      // Simple Schedules
      .addCase(fetchSchedulesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchedulesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.schedules = action.payload.schedules;
      })
      .addCase(fetchSchedulesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchSchedulesWithValvesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchedulesWithValvesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.schedulesWithValves = action.payload;
      })
      .addCase(fetchSchedulesWithValvesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchScheduleAsync.fulfilled, (state, action) => {
        state.selectedSchedule = action.payload;
      })
      .addCase(createScheduleAsync.fulfilled, (state, action) => {
        state.schedules.push(action.payload);
      })
      .addCase(updateScheduleAsync.fulfilled, (state, action) => {
        const index = state.schedules.findIndex(schedule => schedule.id === action.payload.id);
        if (index !== -1) {
          state.schedules[index] = action.payload;
        }
        // Update selected schedule if it's the one being updated
        if (state.selectedSchedule?.id === action.payload.id) {
          state.selectedSchedule = action.payload;
        }
      })
      .addCase(deleteScheduleAsync.fulfilled, (state, action) => {
        state.schedules = state.schedules.filter(schedule => schedule.id !== action.payload);
        state.schedulesWithValves = state.schedulesWithValves.filter(schedule => schedule.id !== action.payload);
        // Clear selected schedule if it's the one being deleted
        if (state.selectedSchedule?.id === action.payload) {
          state.selectedSchedule = null;
        }
      })
      .addCase(toggleScheduleAsync.fulfilled, (state, action) => {
        const { scheduleId, active } = action.payload;
        const index = state.schedules.findIndex(schedule => schedule.id === scheduleId);
        if (index !== -1) {
          state.schedules[index].active = active;
        }
        // Update schedules with valves
        const valvesIndex = state.schedulesWithValves.findIndex(schedule => schedule.id === scheduleId);
        if (valvesIndex !== -1) {
          state.schedulesWithValves[valvesIndex].active = active;
        }
        // Update selected schedule if it's the one being toggled
        if (state.selectedSchedule?.id === scheduleId) {
          state.selectedSchedule.active = active;
        }
      })
      .addCase(validateScheduleAsync.fulfilled, (state, action) => {
        // Store validation result with a temporary key
        state.validationResults['temp'] = action.payload;
      })
      .addCase(validateExistingScheduleAsync.fulfilled, (state, action) => {
        const { scheduleId, result } = action.payload;
        state.validationResults[scheduleId] = result;
      })
      .addCase(fetchValvesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValvesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.valves = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchValvesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Legacy Device Groups (backward compatibility)
      .addCase(fetchDeviceGroupsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeviceGroupsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchDeviceGroupsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedSchedule,
  setFilters,
  clearValidationResults,
  setConnectionStatus,
  updateScheduleFromWebSocket,
  removeScheduleFromWebSocket,
} = scheduleSlice.actions;

// Selectors
export const selectSchedules = (state: { schedules: ScheduleState }) => state.schedules.schedules;
export const selectSchedulesWithValves = (state: { schedules: ScheduleState }) => state.schedules.schedulesWithValves;
export const selectValves = (state: { schedules: ScheduleState }) => state.schedules.valves;
export const selectScheduleLoading = (state: { schedules: ScheduleState }) => state.schedules.loading;
export const selectScheduleError = (state: { schedules: ScheduleState }) => state.schedules.error;
export const selectSelectedSchedule = (state: { schedules: ScheduleState }) => state.schedules.selectedSchedule;
export const selectScheduleFilters = (state: { schedules: ScheduleState }) => state.schedules.filters;
export const selectValidationResults = (state: { schedules: ScheduleState }) => state.schedules.validationResults;
export const selectConnectionStatus = (state: { schedules: ScheduleState }) => state.schedules.connectionStatus;
export const selectLastUpdated = (state: { schedules: ScheduleState }) => state.schedules.lastUpdated;

// Filtered schedules selector
export const selectFilteredSchedules = (state: { schedules: ScheduleState }) => {
  const { schedules, filters } = state.schedules;

  return schedules.filter(schedule => {
    // Filter by active status
    if (filters.activeOnly && !schedule.active) {
      return false;
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      return (
        schedule.name.toLowerCase().includes(searchLower) ||
        schedule.startTime.includes(searchLower) ||
        schedule.days.some(day => day.toLowerCase().includes(searchLower))
      );
    }

    // Filter by selected days
    if (filters.selectedDays.length > 0) {
      return schedule.days.some(day => filters.selectedDays.includes(day));
    }

    return true;
  });
};

// Active schedules selector for dashboard
export const selectActiveSchedules = (state: { schedules: ScheduleState }) => {
  return state.schedules.schedules.filter(schedule => schedule.active);
};

export default scheduleSlice.reducer;